import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Truck, Calendar, FileDown } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";

// A cycling palette so each vendor gets a stable colour across the chart + table.
const PALETTE = [
  "#7A3E1F", "#1A4DFF", "#FFB800", "#FF3333", "#0A0A0A",
  "#0E9F6E", "#8B5CF6", "#E11D48", "#0EA5E9", "#F97316",
];
const monthLabel = (m) => {
  // m is "YYYY-MM"
  if (!m || m.length < 7) return m || "";
  const [y, mm] = m.split("-");
  const d = new Date(Number(y), Number(mm) - 1, 1);
  return d.toLocaleString("en-GB", { month: "short", year: "2-digit" });
};

const todayIST = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return d.toISOString().slice(0, 10);
};
const firstOfYear = () => {
  const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  return `${d.getFullYear()}-01-01`;
};

export default function Vendors() {
  const [data, setData] = useState({ months: [], vendors: [], grand_total: 0 });
  const [dateFrom, setDateFrom] = useState(firstOfYear());
  const [dateTo, setDateTo] = useState(todayIST());
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/purchase-orders/vendor-stats", { params });
      setData(res.data || { months: [], vendors: [], grand_total: 0 });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  }
  // eslint-disable-next-line
  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const chartData = useMemo(() => {
    return (data.months || []).map((m) => {
      const row = { month: monthLabel(m), _key: m };
      for (const v of data.vendors || []) {
        row[v.vendor] = v.monthly?.[m] || 0;
      }
      return row;
    });
  }, [data]);

  const topVendor = (data.vendors || [])[0] || null;

  function downloadCsv() {
    const months = data.months || [];
    const header = ["Vendor", ...months.map(monthLabel), "Total Units"];
    const rows = (data.vendors || []).map((v) => [
      v.vendor,
      ...months.map((m) => v.monthly?.[m] || 0),
      v.total_units,
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `closet-vendors-${dateFrom || "all"}-to-${dateTo || "all"}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 800);
    toast.success("Vendor breakdown CSV downloaded.");
  }

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="vendors-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-2">/ INSIGHTS · SUPPLIERS</div>
          <h1 className="section-title text-5xl lg:text-6xl">Vendors.</h1>
          <p className="text-zinc-600 mt-3 text-sm max-w-xl">Units purchased per vendor per month — sourced from Purchase Orders. Spot who's really shipping the most tees.</p>
        </div>
        <button onClick={downloadCsv} className="brut-btn-outline px-5 py-2.5 inline-flex items-center gap-2 self-start" data-testid="download-vendors-csv-btn">
          <FileDown size={16} /> Download CSV
        </button>
      </header>

      {/* Filter strip */}
      <div className="brut-card p-4 mb-5 flex flex-wrap items-center gap-3" data-testid="vendors-filter-strip">
        <Calendar size={14} className="text-zinc-500" />
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">From</div>
        <input type="date" className="brut-input font-mono-ds max-w-[180px]" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="vendors-date-from" />
        <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">To</div>
        <input type="date" className="brut-input font-mono-ds max-w-[180px]" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="vendors-date-to" />
        <button
          onClick={() => { setDateFrom(""); setDateTo(""); }}
          className="px-3 py-2 border-2 border-black font-mono-ds text-[11px] uppercase tracking-wider bg-white hover:bg-zinc-100"
          data-testid="vendors-clear-dates-btn"
        >All Time</button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard testid="vendors-stat-vendors" label="Vendors" value={(data.vendors || []).length} />
        <StatCard testid="vendors-stat-units"   label="Total Units" value={(data.grand_total || 0).toLocaleString("en-IN")} accent />
        <StatCard testid="vendors-stat-top"     label="Top Vendor"
          value={topVendor ? topVendor.vendor : "—"}
          sub={topVendor ? `${topVendor.total_units.toLocaleString("en-IN")} units` : "no data"} />
      </div>

      {/* Chart */}
      <div className="brut-card p-5 mb-6" data-testid="vendors-chart-card">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Units per Vendor · Monthly</div>
          <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">{loading ? "Loading…" : `${chartData.length} month${chartData.length === 1 ? "" : "s"}`}</div>
        </div>
        {chartData.length === 0 ? (
          <div className="text-center py-16 text-zinc-500 font-mono-ds text-xs uppercase" data-testid="vendors-empty">
            <Truck size={28} className="mx-auto mb-2 opacity-40" />
            No purchase orders in this range · adjust the filter to see vendor data
          </div>
        ) : (
          <div style={{ width: "100%", height: 380 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a20" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "monospace" }} />
                <YAxis tick={{ fontSize: 11, fontFamily: "monospace" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ border: "2px solid #0a0a0a", background: "#FDF6EE", fontFamily: "monospace", fontSize: 12 }}
                  cursor={{ fill: "#0a0a0a10" }}
                />
                <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 11 }} />
                {(data.vendors || []).map((v, i) => (
                  <Bar key={v.vendor} dataKey={v.vendor} stackId="a" fill={PALETTE[i % PALETTE.length]} stroke="#0a0a0a" strokeWidth={1} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Vendor × month matrix table */}
      <div className="brut-card overflow-x-auto" data-testid="vendors-matrix-card">
        <table className="brut-table min-w-[900px]">
          <thead>
            <tr>
              <th className="text-left min-w-[180px]">Vendor</th>
              {(data.months || []).map((m) => (
                <th key={m} className="text-right font-mono-ds">{monthLabel(m)}</th>
              ))}
              <th className="text-right font-mono-ds">Total</th>
            </tr>
          </thead>
          <tbody>
            {(data.vendors || []).map((v, i) => (
              <tr key={v.vendor} data-testid={`vendors-row-${v.vendor}`}>
                <td className="font-display font-bold">
                  <span className="inline-block w-3 h-3 border-2 border-black mr-2 align-middle" style={{ background: PALETTE[i % PALETTE.length] }} />
                  {v.vendor}
                </td>
                {(data.months || []).map((m) => (
                  <td key={m} className="text-right font-mono-ds">{(v.monthly?.[m] || 0).toLocaleString("en-IN")}</td>
                ))}
                <td className="text-right font-mono-ds font-black">{v.total_units.toLocaleString("en-IN")}</td>
              </tr>
            ))}
            {(data.vendors || []).length === 0 && (
              <tr><td colSpan={(data.months || []).length + 2} className="text-center py-10 text-zinc-500 font-mono-ds text-xs uppercase">No vendor data · create a purchase order first</td></tr>
            )}
          </tbody>
          {(data.vendors || []).length > 0 && (
            <tfoot>
              <tr className="bg-black text-white font-display font-black">
                <td className="font-mono-ds text-[11px] tracking-widest uppercase">Column Totals</td>
                {(data.months || []).map((m) => {
                  const total = (data.vendors || []).reduce((a, v) => a + (v.monthly?.[m] || 0), 0);
                  return <td key={m} className="text-right font-mono-ds">{total.toLocaleString("en-IN")}</td>;
                })}
                <td className="text-right font-mono-ds" data-testid="vendors-grand-total">{(data.grand_total || 0).toLocaleString("en-IN")}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent, testid }) {
  return (
    <div className={`brut-card p-5 ${accent ? "bg-black text-white" : "bg-white"}`} data-testid={testid}>
      <div className={`font-mono-ds text-[10px] tracking-widest uppercase ${accent ? "text-white/70" : "text-zinc-500"}`}>{label}</div>
      <div className="font-display font-black text-3xl leading-tight mt-1 truncate">{value}</div>
      {sub && <div className={`font-mono-ds text-[10px] mt-1 ${accent ? "text-white/70" : "text-zinc-500"}`}>{sub}</div>}
    </div>
  );
}
